// ═══════════════════════════════════════════════════════════════════════════════
// Helper home (/home) — UI aligned with UI/helper/Home.png
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  APPROVAL_STATUS,
  LANGUAGE,
  TASK_KIND,
  USER_ROLE,
  type BudgetListItem,
  type PinMessage,
  type SettlementListItem,
  type SystemMessage,
  type TodayTodoItem,
} from '@fdh/shared';
import { EmployerStatusRibbon } from '../../components/task/employer/EmployerStatusRibbon';
import { WeatherGreetingHeadingEmoji } from '../../components/home/WeatherGreetingHeadingEmoji';
import { WeatherGreetingSubheading } from '../../components/home/WeatherGreetingSubheading';
import { HelperOverdueTasksSummaryCard } from '../../components/home/HelperOverdueTasksSummaryCard';
import { HelperScanReceiptActionCard } from '../../components/home/HelperScanReceiptActionCard';
import { HelperTomorrowTasksActionCard } from '../../components/home/HelperTomorrowTasksActionCard';
import { MoneyGivenCashWatermark } from '../../components/home/MoneyGivenCashWatermark';
import { EmployerTodayCompletionArcGauge } from '../../components/home/EmployerTodayCompletionArcGauge';
import { PendingReviewClipboardWatermark } from '../../components/home/PendingReviewClipboardWatermark';
import { EmployerTimeBucketIcon } from '../../components/task/employer/EmployerTimeBucketIcon';
import { ListNavRow } from '../../components/ui/ListNavRow';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { budgetApi, pinMessageApi, settlementApi, systemMessageApi, taskInstanceApi } from '../../lib/api';
import { isHelperBudgetPeriodSettleable } from '../../utils/employerMoneyGivenSettlement';
import { PinMessageTicker } from '../../components/pinMessage/PinMessageTicker';
import { SystemMessageTicker } from '../../components/systemMessage/SystemMessageTicker';
import {
  EMPLOYER_TIME_BUCKET_I18N,
  groupTasksByTimeBucket,
} from '../../utils/employerTaskDayGrouping';
import {
  getCurrentHourInTimezone,
  resolveGreetingI18nKey,
  resolveGreetingTimeBucket,
} from '../../lib/greetingTimeOfDay';
import { addCalendarDaysToYmd } from '../../utils/taskEarlySubmit';
import { formatMoneyDisplay } from '../../lib/formatMoneyDisplay';
import {
  AutoTranslationHint,
  resolveTodoItemDisplayTitle,
  resolveTranslationTargetLanguageLabel,
  todoListHasTranslations,
} from '../../components/translation';

const pickPreferredBudget = (rows: BudgetListItem[], todayYmd: string): BudgetListItem | undefined => {
  const currentPeriodBudget = rows.find((row) => row.startDate <= todayYmd && row.endDate >= todayYmd);
  if (currentPeriodBudget) {
    return currentPeriodBudget;
  }
  const targetMs = new Date(`${todayYmd}T00:00:00`).getTime();
  return [...rows].sort((a, b) => {
    const aDiff = Math.abs(new Date(`${a.startDate}T00:00:00`).getTime() - targetMs);
    const bDiff = Math.abs(new Date(`${b.startDate}T00:00:00`).getTime() - targetMs);
    if (aDiff !== bDiff) {
      return aDiff - bDiff;
    }
    return a.startDate.localeCompare(b.startDate);
  })[0];
};

const getTodayYmdByTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '1970';
  const month = parts.find((part) => part.type === 'month')?.value ?? '01';
  const day = parts.find((part) => part.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
};

const getCurrentMinutesByTimezone = (timezone: string): number => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
  return hour * 60 + minute;
};

const parseDueTimeToMinutes = (dueTime: string): number | null => {
  const trimmed = dueTime.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return hour * 60 + minute;
};


export const HelperHomePage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pinMessageIdFromNotify = searchParams.get('pinMessageId') ?? '';
  const handleInitialPinMessageOpened = useCallback((): void => {
    if (!searchParams.has('pinMessageId')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('pinMessageId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [todayTasks, setTodayTasks] = useState<TodayTodoItem[]>([]);
  const [tomorrowCount, setTomorrowCount] = useState(0);
  const [overdueTaskCount, setOverdueTaskCount] = useState<number | null>(null);
  const [activeMainBudget, setActiveMainBudget] = useState<BudgetListItem | null>(null);
  const [settlements, setSettlements] = useState<SettlementListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pinMessages, setPinMessages] = useState<PinMessage[]>([]);
  const [pinMessageRotationSeconds, setPinMessageRotationSeconds] = useState(5);
  const [systemMessages, setSystemMessages] = useState<SystemMessage[]>([]);
  const [systemMessageRotationSeconds, setSystemMessageRotationSeconds] = useState(5);
  const [pinShowOriginalGlobal, setPinShowOriginalGlobal] = useState(false);
  const [showTaskContentOriginal, setShowTaskContentOriginal] = useState(false);

  useEffect(() => {
    setShowTaskContentOriginal(false);
  }, [language]);

  const timezone = useMemo(
    () => resolveDisplayTimeZone(user?.settings.timezone),
    [user?.settings.timezone]
  );
  const todayYmd = useMemo(() => getTodayYmdByTimezone(timezone), [timezone]);

  const greetingHour = useMemo(() => getCurrentHourInTimezone(timezone), [timezone]);

  const greetingBucket = useMemo(() => resolveGreetingTimeBucket(greetingHour), [greetingHour]);

  const greetingLead = useMemo(
    () => t(resolveGreetingI18nKey(greetingBucket, 'helper'), { name: user?.nickname ?? '' }),
    [greetingBucket, t, user?.nickname],
  );

  const loadPinMessages = useCallback(async () => {
    try {
      const [pinMessageRes, systemMessageRes] = await Promise.all([
        pinMessageApi.list(false, language),
        systemMessageApi.list(language, true),
      ]);
      if (pinMessageRes.success && pinMessageRes.data) {
        setPinMessages(pinMessageRes.data.items ?? []);
        setPinMessageRotationSeconds(pinMessageRes.data.rotationSeconds ?? 5);
      } else {
        setPinMessages([]);
        setPinMessageRotationSeconds(5);
      }
      if (systemMessageRes.success && systemMessageRes.data) {
        setSystemMessages(systemMessageRes.data.items ?? []);
        setSystemMessageRotationSeconds(systemMessageRes.data.rotationSeconds ?? 5);
      } else {
        setSystemMessages([]);
        setSystemMessageRotationSeconds(5);
      }
    } catch {
      setPinMessages([]);
      setPinMessageRotationSeconds(5);
      setSystemMessages([]);
      setSystemMessageRotationSeconds(5);
    }
  }, [language]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [todayRes, tomorrowCountRes, overdueRes] = await Promise.all([
        taskInstanceApi.today(language),
        taskInstanceApi.tomorrowCount(),
        taskInstanceApi.helperOverdueCount(),
      ]);

      if (!todayRes.success || !todayRes.data) {
        setError(todayRes.error ?? t('helperHome.loadError'));
        return;
      }

      setTodayTasks(todayRes.data.tasks ?? []);
      if (tomorrowCountRes.success && tomorrowCountRes.data) {
        setTomorrowCount(tomorrowCountRes.data.total ?? 0);
      } else {
        setTomorrowCount(0);
      }
      if (overdueRes.success && overdueRes.data) {
        setOverdueTaskCount(overdueRes.data.total ?? 0);
      } else {
        setOverdueTaskCount(null);
      }

      try {
        const [budgetRes, settlementRes] = await Promise.all([
          budgetApi.list('active', 1, 100),
          settlementApi.list('all', 1, 100),
        ]);
        if (settlementRes.success && settlementRes.data) {
          const settlementRows = Array.isArray(settlementRes.data)
            ? settlementRes.data
            : (settlementRes.data.items ?? []);
          setSettlements(settlementRows);
        } else {
          setSettlements([]);
        }
        if (budgetRes.success && budgetRes.data) {
          const rows = Array.isArray(budgetRes.data) ? budgetRes.data : (budgetRes.data.items ?? []);
          const preferredBudget = pickPreferredBudget(rows, todayYmd);
          setActiveMainBudget(preferredBudget ?? null);
        } else {
          setActiveMainBudget(null);
        }
      } catch {
        setActiveMainBudget(null);
        setSettlements([]);
      }

    } catch {
      setError(t('helperHome.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, todayYmd, language]);

  useEffect(() => {
    setPinShowOriginalGlobal(false);
  }, [language]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadPinMessages();
  }, [loadPinMessages]);

  const previewTasks = useMemo(() => todayTasks, [todayTasks]);
  const previewHasTranslations = useMemo(() => todoListHasTranslations(previewTasks), [previewTasks]);
  const previewTargetLanguageLabel = useMemo(() => {
    const first = previewTasks.find((task) => task.translation?.hasTranslation);
    return resolveTranslationTargetLanguageLabel(
      t,
      first?.translation?.targetLanguage,
      language,
    );
  }, [language, previewTasks, t]);
  const groupedPreviewTasks = useMemo(
    () => groupTasksByTimeBucket(previewTasks),
    [previewTasks],
  );

  const totalTodayTasks = todayTasks.length;
  const doneTodayTasks = todayTasks.filter((x) => {
    if (x.status === APPROVAL_STATUS.APPROVED) {
      return true;
    }
    if (x.status !== APPROVAL_STATUS.PENDING) {
      return false;
    }
    const submitted = x.submittedAt && String(x.submittedAt).trim() !== '';
    return Boolean(submitted);
  }).length;
  const currentMinutes = useMemo(() => getCurrentMinutesByTimezone(timezone), [timezone, loading]);

  const isAwaitingSubmission = (task: TodayTodoItem): boolean =>
    task.status === APPROVAL_STATUS.PENDING &&
    (!task.submittedAt || String(task.submittedAt).trim() === '');

  const isTaskSubmittedOrApproved = (task: TodayTodoItem): boolean =>
    task.status === APPROVAL_STATUS.APPROVED ||
    (task.status === APPROVAL_STATUS.PENDING &&
      Boolean(task.submittedAt && String(task.submittedAt).trim() !== ''));

  const isTaskOverdueTime = (task: TodayTodoItem): boolean => {
    if (!isAwaitingSubmission(task)) {
      return false;
    }
    if (!task.dueTime || task.dueDate !== todayYmd) {
      return false;
    }
    const dueMinutes = parseDueTimeToMinutes(task.dueTime);
    if (dueMinutes === null) {
      return false;
    }
    return dueMinutes < currentMinutes;
  };

  const handleOpenTodayTasksPage = () => {
    navigate('/today');
  };

  const handleTodayTasksCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenTodayTasksPage();
    }
  };

  const handleOverdueTasksClick = () => {
    navigate('/today?tab=overdue');
  };

  const handleTomorrowClick = () => {
    const tomorrow = addCalendarDaysToYmd(todayYmd, 1);
    navigate(
      `/today?tab=future&futStart=${encodeURIComponent(tomorrow)}&futEnd=${encodeURIComponent(tomorrow)}`,
    );
  };

  const isActiveBudgetSettleable = useMemo(() => {
    if (!activeMainBudget) {
      return false;
    }
    return isHelperBudgetPeriodSettleable(activeMainBudget, settlements);
  }, [activeMainBudget, settlements]);

  const handleMoneyReceivedActionClick = () => {
    if (!activeMainBudget) {
      navigate('/settlements');
      return;
    }
    const search = new URLSearchParams({ mainBudgetId: activeMainBudget.id }).toString();
    if (isActiveBudgetSettleable) {
      navigate(`/settlements/confirm?${search}`);
      return;
    }
    navigate(`/settlements?${search}`);
  };

  const handleMoneyReceivedActionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMoneyReceivedActionClick();
    }
  };

  if (!user) {
    return null;
  }
  if (user.role !== USER_ROLE.HELPER) {
    return <Navigate to="/dashboard" replace />;
  }

  const hasBudgetData = activeMainBudget !== null;
  const activeBudgetRange = activeMainBudget
    ? t('settlement.period', {
        start: activeMainBudget.startDate.slice(5, 10),
        end: activeMainBudget.endDate.slice(5, 10),
      })
    : '';
  const budgetSpent = activeMainBudget?.totalSpent ?? 0;
  const budgetAmount = activeMainBudget?.amount ?? 0;
  const budgetRemaining = activeMainBudget?.remaining ?? 0;
  const moneyReceivedActionLabel = isActiveBudgetSettleable
    ? t('helperHome.actionSettlement')
    : t('helperHome.actionViewDetails');
  const isBudgetOverspent = budgetRemaining < 0;
  const budgetStatusAmount = Math.abs(budgetRemaining);
  const budgetCardClassName = isBudgetOverspent
    ? 'relative overflow-hidden rounded-3xl border border-rose-300 bg-gradient-to-br from-white via-rose-50/80 to-pink-100/70 p-4 shadow-sm min-[400px]:p-3 sm:p-4 dark:border-rose-300 dark:bg-gradient-to-br dark:from-gray-900 dark:via-rose-950/35 dark:to-gray-900'
    : 'relative overflow-hidden rounded-3xl border border-primary-200 bg-gradient-to-br from-white via-emerald-50/80 to-emerald-100/80 p-4 shadow-sm min-[400px]:p-3 sm:p-4 dark:border-emerald-300 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900 dark:to-emerald-950/60';
  const budgetCardDividerClassName = isBudgetOverspent
    ? 'mt-4 border-t border-rose-200/70 pt-3 text-center dark:border-gray-700/60'
    : 'mt-4 border-t border-emerald-200/70 pt-3 text-center dark:border-gray-700/60';
  const budgetTitleIconClassName = isBudgetOverspent ? 'h-5 w-5 text-rose-700 dark:text-rose-300' : 'h-5 w-5 text-emerald-600';
  const budgetOverspentTextClassName = 'font-semibold text-rose-800 dark:text-rose-300';
  const budgetOverspentAmountClassName =
    'text-[30px] font-bold leading-none text-rose-800 min-[400px]:text-2xl sm:text-[30px] dark:text-rose-300';
  const budgetOverspentBarClassName = 'h-full rounded-full bg-rose-800 dark:bg-rose-400';
  const budgetSettleButtonClassName = isBudgetOverspent
    ? 'text-sm font-semibold text-rose-800 min-[400px]:text-xs sm:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 dark:text-rose-300'
    : 'text-sm font-semibold text-primary-700 min-[400px]:text-xs sm:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-400';
  const budgetProgressPercent = budgetAmount <= 0
    ? 100
    : Math.min((budgetSpent / budgetAmount) * 100, 100);
  const submittedTodayTasks = doneTodayTasks;
  const todaySubmitPercent =
    totalTodayTasks === 0
      ? 0
      : Math.round((submittedTodayTasks / Math.max(totalTodayTasks, 1)) * 100);

  return (
    <main className="px-4 py-5">
      <div className="mb-5">
        <SystemMessageTicker
          items={systemMessages}
          rotationSeconds={systemMessageRotationSeconds}
          onOpenDetail={(item) => {
            if (!item.isRead) {
              void systemMessageApi.markRead(item.id).then(() => {
                setSystemMessages((prev) =>
                  prev.map((row) => (row.id === item.id ? { ...row, isRead: true } : row)),
                );
              });
            }
          }}
        />
        <div className="flex items-center gap-2.5">
          <WeatherGreetingHeadingEmoji hour={greetingHour} />
          <p className="text-2xl font-bold leading-tight tracking-tight text-gray-900">{greetingLead}</p>
        </div>
        <WeatherGreetingSubheading />
        <PinMessageTicker
          items={pinMessages}
          rotationSeconds={pinMessageRotationSeconds}
          initialPinMessageId={pinMessageIdFromNotify}
          onInitialPinMessageOpened={handleInitialPinMessageOpened}
          enableHelperTranslation
          showOriginalGlobal={pinShowOriginalGlobal}
          onToggleShowOriginalGlobal={() => setPinShowOriginalGlobal((prev) => !prev)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12" role="status" aria-label={t('common.loading')}>
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      ) : null}

      {!loading && error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700" role="alert">
          {error}
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 block w-full font-semibold text-primary-700 underline"
          >
            {t('task.action.refresh')}
          </button>
        </div>
      ) : null}

      {!loading && !error ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
            <section
              role="button"
              tabIndex={0}
              aria-label={t('helperHome.ariaTodayCompletionCard')}
              onClick={handleOpenTodayTasksPage}
              onKeyDown={handleTodayTasksCardKeyDown}
              className="relative flex min-h-full min-w-0 cursor-pointer flex-col items-center overflow-hidden rounded-3xl border border-primary-200 bg-white p-4 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-[400px]:p-3 sm:p-4 dark:border-gray-700 dark:bg-gray-900/80"
              aria-labelledby="helper-today-completion-heading"
            >
              <div
                className="pointer-events-none absolute -right-8 -top-6 opacity-[0.6] dark:opacity-[0.45]"
                aria-hidden
              >
                <PendingReviewClipboardWatermark className="h-[200px] w-[340px] max-w-none" />
              </div>
              <div className="relative z-10 flex w-full flex-col items-center">
                <h2
                  id="helper-today-completion-heading"
                  className="mb-1 w-full shrink-0 text-center text-sm font-semibold text-gray-900 min-[400px]:text-sm sm:text-sm dark:text-gray-100"
                >
                  {t('helperHome.todayCompletion')}
                </h2>
                <EmployerTodayCompletionArcGauge
                  percent={todaySubmitPercent}
                  dateYmd={todayYmd}
                  completionFractionLabel={t('helperHome.submittedFraction', {
                    done: submittedTodayTasks,
                    total: totalTodayTasks,
                  })}
                  completionFractionValue={t('helperHome.submittedFractionValue', {
                    done: submittedTodayTasks,
                    total: totalTodayTasks,
                  })}
                  completionFractionStatus={t('helperHome.submittedFractionStatus')}
                  completionFractionSubLabel={
                    language === LANGUAGE.EN ||
                    language === LANGUAGE.ID ||
                    language === LANGUAGE.FIL
                      ? t('helperHome.submittedFractionStatus')
                      : undefined
                  }
                />
              </div>
            </section>
            <HelperOverdueTasksSummaryCard
              count={overdueTaskCount}
              title={t('helperHome.cardOverdueTasks')}
              hint={t('helperHome.overdueTasksHint')}
              placeholderLabel={t('helperHome.dataPlaceholder')}
              ariaLabel={t('helperHome.ariaOverdueTasksCard')}
              onClick={handleOverdueTasksClick}
            />
          </div>

          <section
            aria-labelledby="helper-money-received-heading"
            className={budgetCardClassName}
          >
            <div
              className="pointer-events-none absolute -right-10 -top-8 opacity-[0.52] dark:opacity-50"
              aria-hidden
            >
              <MoneyGivenCashWatermark tone="subtle" className="h-[200px] w-[280px] max-w-none" />
            </div>
            <div className="relative z-10">
            <div className="mb-2 flex items-start justify-between gap-2">
              <h2
                id="helper-money-received-heading"
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 min-[400px]:text-sm sm:text-sm dark:text-gray-100"
              >
                <svg
                  className={budgetTitleIconClassName}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h10a2 2 0 012 2v8a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 11h10a2 2 0 012 2v2"
                  />
                </svg>
                {t('helperHome.weeklyBudgetTitle', { range: activeBudgetRange })}
              </h2>
            </div>
            {!hasBudgetData ? (
              <p className="text-sm text-gray-500 dark:text-gray-300">{t('helperHome.weeklyBudgetPlaceholder')}</p>
            ) : (
              <>
                <div className="mt-1 flex items-end justify-between gap-2">
                  <div
                    className="grid items-end gap-x-1"
                    style={{ gridTemplateColumns: 'auto auto auto' }}
                  >
                    <span className="col-start-1 row-start-1 text-right text-sm text-gray-600 min-[400px]:text-xs min-[400px]:max-sm:leading-snug sm:text-sm dark:text-gray-200">
                      {t('helperHome.budgetSpentLabel')}
                    </span>
                    <span
                      className="col-start-2 row-start-1 self-end px-0.5 text-sm text-gray-600 min-[400px]:text-xs sm:text-sm dark:text-gray-200"
                      aria-hidden
                    >
                      /
                    </span>
                    <span className="col-start-3 row-start-1 text-sm text-gray-600 min-[400px]:text-xs min-[400px]:max-sm:leading-snug sm:text-sm dark:text-gray-200">
                      {t('helperHome.budgetReceivedLabel')}
                    </span>
                    <span className="col-start-1 row-start-2 text-right text-[30px] font-bold leading-none text-gray-900 min-[400px]:text-2xl sm:text-[30px] dark:text-gray-100">
                      {formatMoneyDisplay(budgetSpent)}
                    </span>
                    <span
                      className="col-start-2 row-start-2 self-end px-0.5 text-[27px] font-semibold leading-none text-gray-500 min-[400px]:text-xl sm:text-[27px] dark:text-gray-300"
                      aria-hidden
                    >
                      /
                    </span>
                    <span className="col-start-3 row-start-2 text-[27px] font-semibold leading-none text-gray-500 min-[400px]:text-xl sm:text-[27px] dark:text-gray-300">
                      {formatMoneyDisplay(budgetAmount)}
                    </span>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span
                      className={`text-sm min-[400px]:text-xs sm:text-sm ${
                        isBudgetOverspent
                          ? budgetOverspentTextClassName
                          : 'font-semibold text-gray-600 dark:text-gray-200'
                      }`}
                    >
                      {isBudgetOverspent
                        ? t('helperHome.budgetOverspentLabel')
                        : t('helperHome.budgetRemainingLabel')}
                    </span>
                    <p
                      className={
                        isBudgetOverspent
                          ? budgetOverspentAmountClassName
                          : 'text-[30px] font-bold leading-none text-emerald-700 min-[400px]:text-2xl sm:text-[30px] dark:text-emerald-300'
                      }
                    >
                      {formatMoneyDisplay(budgetStatusAmount)}
                    </p>
                  </div>
                </div>
                <div className="mt-3 h-3 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700/70">
                  <div
                    className={isBudgetOverspent ? budgetOverspentBarClassName : 'h-full rounded-full bg-emerald-700'}
                    style={{ width: `${budgetProgressPercent}%` }}
                  />
                </div>
              </>
            )}
            <div className={budgetCardDividerClassName}>
              <button
                type="button"
                tabIndex={0}
                onClick={handleMoneyReceivedActionClick}
                onKeyDown={handleMoneyReceivedActionKeyDown}
                aria-label={moneyReceivedActionLabel}
                className={budgetSettleButtonClassName}
              >
                {moneyReceivedActionLabel}
              </button>
            </div>
            </div>
          </section>

          <div className="grid grid-cols-2 gap-3">
            <HelperScanReceiptActionCard
              to="/receipts/analyze"
              title={t('helperHome.actionScanReceipt')}
              hint={t('helperHome.actionScanReceiptSub')}
              ariaLabel={t('helperHome.actionScanReceipt')}
            />
            <HelperTomorrowTasksActionCard
              count={tomorrowCount}
              title={t('helperHome.actionTomorrow')}
              hint={t('helperHome.actionTomorrowSub')}
              ariaLabel={t('helperHome.actionTomorrow')}
              onClick={handleTomorrowClick}
            />
          </div>

          <section
            id="helper-today-preview"
            aria-labelledby="helper-preview-heading"
            className="border-t border-gray-200/80 pt-4 dark:border-gray-700/60"
          >
            <h2 id="helper-preview-heading" className="mb-3 flex items-center gap-2 text-base font-bold text-gray-900">
              <svg
                className="h-5 w-5 text-emerald-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5h10M9 12h10M9 19h10M4 5l1.5 1.5L7.5 4M4 12l1.5 1.5L7.5 11M4 19l1.5 1.5L7.5 18"
                />
              </svg>
              {t('helperHome.todayPreviewTitle')}
              {previewHasTranslations ? (
                <AutoTranslationHint
                  targetLanguageLabel={previewTargetLanguageLabel}
                  showingOriginal={showTaskContentOriginal}
                  onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
                  className="ml-1"
                />
              ) : null}
            </h2>
            {previewTasks.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('task.today.empty')}</p>
            ) : (
              <div className="space-y-6">
                {groupedPreviewTasks.map((group) => (
                  <section key={group.bucket} aria-labelledby={`helper-preview-bucket-${group.bucket}`}>
                    <h3
                      id={`helper-preview-bucket-${group.bucket}`}
                      className="mb-3 flex items-center gap-2.5"
                    >
                      <EmployerTimeBucketIcon bucket={group.bucket} />
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {t(EMPLOYER_TIME_BUCKET_I18N[group.bucket])}
                      </span>
                    </h3>
                    <ul className="flex flex-col gap-3" role="list">
                      {group.tasks.map((task) => {
                        const isDone = isTaskSubmittedOrApproved(task);
                        const displayTitle = resolveTodoItemDisplayTitle(task, showTaskContentOriginal);
                        return (
                        <li key={task.taskInstanceId}>
                          <ListNavRow
                            ariaLabel={
                              task.taskKind === TASK_KIND.INVENTORY
                                ? `${displayTitle} (${t('task.list.kindStockTake')})`
                                : displayTitle
                            }
                            comfortable
                            ribbon={<EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt} />}
                            leading={
                              <div
                                className={`flex h-[42px] w-[80px] flex-col items-center justify-center rounded-xl px-2 py-1.5 ${
                                  isTaskOverdueTime(task)
                                    ? 'bg-red-600 text-white dark:bg-red-700'
                                    : task.status === APPROVAL_STATUS.APPROVED ||
                                        (task.status === APPROVAL_STATUS.PENDING && !isAwaitingSubmission(task))
                                      ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                                      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100'
                                }`}
                              >
                                <p
                                  className={`whitespace-nowrap font-bold leading-none tracking-tight ${
                                    task.dueTime ? 'text-[26px] tabular-nums' : 'text-xl'
                                  } ${isDone ? 'line-through decoration-gray-600 dark:decoration-gray-500' : ''}`}
                                >
                                  {task.dueTime || t('task.todo.deadlineToday')}
                                </p>
                              </div>
                            }
                            onClick={() => {
                              const inventoryReadyToWizard =
                                task.taskKind === TASK_KIND.INVENTORY &&
                                task.dueDate <= todayYmd &&
                                (task.status === APPROVAL_STATUS.NEEDS_UPDATE ||
                                  (task.status === APPROVAL_STATUS.PENDING &&
                                    (!task.submittedAt || String(task.submittedAt).trim() === '')));
                              navigate(
                                inventoryReadyToWizard
                                  ? `/tasks/inventory/${task.taskInstanceId}`
                                  : `/task-instances/${task.taskInstanceId}`,
                              );
                            }}
                          >
                            <div className="min-w-0 pr-10">
                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <p
                                  className={`truncate text-sm font-semibold leading-snug ${
                                    isDone
                                      ? 'text-gray-500 line-through decoration-gray-600 dark:text-gray-400 dark:decoration-gray-500'
                                      : 'text-gray-900 dark:text-gray-100'
                                  }`}
                                >
                                  {displayTitle}
                                </p>
                                {task.taskKind === TASK_KIND.INVENTORY ? (
                                  <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                                    {t('task.list.kindStockTake')}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </ListNavRow>
                        </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate('/today')}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50 py-3 text-sm font-semibold text-primary-800 transition-colors hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-primary-700 dark:bg-primary-950/50 dark:text-primary-300 dark:hover:bg-primary-900/60"
            >
              {t('helperHome.viewAllTasks')}
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </section>
        </div>
      ) : null}

    </main>
  );
};
